/**
 * VitalsTable.jsx
 *
 * Displays vitals history with audit-safe editing.
 * FIX: Ensures residentId is always passed to update mutation.
 */

import React, { useContext, useEffect, useState } from "react";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { gql, useQuery, useMutation } from "@apollo/client";
import { UserContext } from "../../../platform/components/auth/auth-context";
import StaffBreak from "../../staffCaregiverHiring/components/business/staff/staffBreak";

/* =========================
   GraphQL
========================= */
const GET_VITALS = gql`
  query GetVitals($residentId: ID!) {
    getVitals(residentId: $residentId) {
      id
      bp
      pulse
      oxygen
      temperature
      respiration
      weight
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_VITAL = gql`
  mutation UpdateVital($id: ID!, $data: VitalsInput!, $reason: String!, $staffId: ID!) {
    updateVital(id: $id, data: $data, reason: $reason, staffId: $staffId) {
      id
      updatedAt
    }
  }
`;

/* =========================
   Helpers
========================= */
const formatDateTime = (iso) =>
    new Date(iso).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

export default function VitalsTable({ residentId, refreshKey }) {
    const { data, loading, refetch } = useQuery(GET_VITALS, {
        variables: { residentId },
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (residentId) {
            refetch();
        }
    }, [refreshKey, residentId, refetch]);

    const userContext = useContext(UserContext);
    const providerUser = userContext.providerUser;

    const [updateVital] = useMutation(UPDATE_VITAL);

    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [form, setForm] = useState({});
    const [editingId, setEditingId] = useState(null);

    if (loading) return <Typography>Loading...</Typography>;

    const rows = data?.getVitals || [];

    /* =========================
       Start Edit
    ========================= */
    const startEdit = (v) => {
        setEditingId(v.id);
        setForm({
            bp: v.bp || "",
            pulse: v.pulse ?? "",
            oxygen: v.oxygen ?? "",
            temperature: v.temperature ?? "",
            respiration: v.respiration ?? "",
            weight: v.weight ?? "",
        });
        setReason("");
        setOpen(true);
    };

    /* =========================
       Submit Edit (FIXED)
    ========================= */
    const submitEdit = async () => {
        if (!reason.trim()) return;

        if (!residentId) {
            console.error("VitalsTable: residentId is missing");
            return;
        }

        await updateVital({
            variables: {
                id: editingId,
                reason,
                data: {
                    residentId,
                    bp: form.bp || null,
                    pulse: form.pulse ? parseInt(form.pulse, 10) : null,
                    oxygen: form.oxygen ? parseInt(form.oxygen, 10) : null,
                    respiration: form.respiration
                        ? parseInt(form.respiration, 10)
                        : null,
                    temperature: form.temperature
                        ? parseFloat(form.temperature)
                        : null,
                    weight: form.weight ? parseFloat(form.weight) : null,
                },
                staffId: providerUser?.staffId || null,
            },
        });

        setOpen(false);
        refetch();
    };



    return (
        <>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Vitals History
            </Typography>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>BP</TableCell>
                        <TableCell>Pulse</TableCell>
                        <TableCell>SpO₂</TableCell>
                        <TableCell>Temp</TableCell>
                        <TableCell>Resp</TableCell>
                        <TableCell>Weight</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((v) => (
                        <TableRow key={v.id}>
                            <TableCell>{formatDateTime(v.createdAt)}</TableCell>
                            <TableCell>{v.bp}</TableCell>
                            <TableCell>{v.pulse}</TableCell>
                            <TableCell>{v.oxygen}</TableCell>
                            <TableCell>{v.temperature}</TableCell>
                            <TableCell>{v.respiration}</TableCell>
                            <TableCell>{v.weight}</TableCell>
                            <TableCell>
                                <IconButton onClick={() => startEdit(v)} size="small">
                                    <EditIcon fontSize="inherit" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* =========================
               Edit Dialog
            ========================= */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Vitals (Reason Required)</DialogTitle>

                <DialogContent>
                    {["bp", "pulse", "oxygen", "temperature", "respiration", "weight"].map(
                        (key) => (
                            <TextField
                                key={key}
                                margin="dense"
                                fullWidth
                                label={key.toUpperCase()}
                                value={form[key]}
                                onChange={(e) =>
                                    setForm({ ...form, [key]: e.target.value })
                                }
                            />
                        )
                    )}

                    <TextField
                        margin="dense"
                        fullWidth
                        required
                        label="Edit Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!reason.trim()}
                        onClick={submitEdit}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
